import { gqlFetcher, graphql } from '#features/api';

export const profileFragment = graphql(`
  fragment ProfileFragment on UserProfile @_unmask {
    about
    avatar
    birthday
    createdAt
    displayName
    gameIds
    gender
    id
    platformsIds
    lastVisit
    updatedAt
    interests {
      id
      name
    }
    languages {
      code
      name
      nativeName
    }
    country {
      code
      icon
      name
    }
    socials {
      id
      displayName
      type
      visible
    }
    # NB: the "identities" field (console gamertags relayed from Discord connections — Xbox,
    # PlayStation, Battle.net) is deliberately NOT requested. The gateway still exposes it, but we
    # do not show other people's linked accounts on the profile; asking for it would also cost two
    # extra downstream calls per profile view (no batch loader on that resolver).
  }
`);

export const getProfileDoc = graphql(
  `
    query UserProfileByDisplayNameOrId($displayNameOrId: String!) {
      user {
        profileByDisplayNameId(displayNameOrId: $displayNameOrId) {
          ...ProfileFragment
        }
      }
    }
  `,
  [profileFragment]
);

export const getProfile = (input: { displayNameOrId: string }) =>
  gqlFetcher(getProfileDoc, {
    displayNameOrId: input.displayNameOrId,
  }).then((data) => data.user.profileByDisplayNameId);

export const getProfileQueryOptions = (input: { displayNameOrId: string }) => ({
  queryKey: ['profile', input.displayNameOrId],
  queryFn: () => getProfile(input),
});

const updateProfileDoc = graphql(`
  mutation UpdateProfile($input: UserProfileInput!) {
    profile {
      updateProfile(input: $input) {
        __typename
        ... on CurrentUser {
          id
          onboarding {
            profileSaved
            postCreated
          }
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            messagesErrors
            name
          }
        }
      }
    }
  }
`);

export type UpdateProfileInput = ReturnType<typeof graphql.scalar<'UserProfileInput'>>;

export const updateProfile = (input: UpdateProfileInput) =>
  gqlFetcher(updateProfileDoc, {
    input,
  }).then((data) => data.profile.updateProfile);

/**
 * Часовой пояс и формат времени — отдельно от анкеты. `updateProfile` переписывает строку
 * профиля целиком, поэтому вызывающий обязан переслать вместе с изменением ВСЕ поля: пропущенный
 * список приезжает на сервер пустым и затирает данные (так три места здесь молча стирали
 * платформы). Эта мутация меняет только то, что передано, а остальное шлюз дочитывает сам —
 * и, в отличие от `updateProfile`, не засчитывает шаг онбординга «профиль заполнен».
 */
const updateTimeSettingsDoc = graphql(`
  mutation UpdateTimeSettings($input: TimeSettingsInput!) {
    profile {
      updateTimeSettings(input: $input) {
        __typename
        ... on CurrentUser {
          id
          timeFormat
          timeZoneOffset
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

export type UpdateTimeSettingsInput = ReturnType<typeof graphql.scalar<'TimeSettingsInput'>>;

export const updateTimeSettings = (input: UpdateTimeSettingsInput) =>
  gqlFetcher(updateTimeSettingsDoc, {
    input,
  }).then((data) => data.profile.updateTimeSettings);

const updateAvatarDoc = graphql(`
  mutation UpdateAvatar($input: UserAvatarInput!) {
    profile {
      updateAvatar(input: $input) {
        __typename
        ... on CurrentUser {
          id
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            messagesErrors
            name
          }
        }
      }
    }
  }
`);

export const updateAvatar = (input: { avatar: string }) =>
  gqlFetcher(updateAvatarDoc, {
    input,
  }).then((data) => data.profile.updateAvatar);

const updateStatsVisibilityDoc = graphql(`
  mutation UpdateStatsVisibility($input: StatsVisibilityInput!) {
    profile {
      updateStatsVisibility(input: $input) {
        __typename
        ... on CurrentUser {
          id
          statsVisibility
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            messagesErrors
            name
          }
        }
      }
    }
  }
`);

export type StatsVisibilityInput = ReturnType<typeof graphql.scalar<'StatsVisibilityInput'>>;

export const updateStatsVisibility = (input: StatsVisibilityInput) =>
  gqlFetcher(updateStatsVisibilityDoc, {
    input,
  }).then((data) => data.profile.updateStatsVisibility);
